import React from "react";
import { useCurrency } from "@/context/CurrencyContext";
import { useLang } from "@/context/LanguageContext";
import { AnimatePresence, motion } from "framer-motion";

const OPTIONS = [
  { key: "AED", label: "AED", ar: "درهم إماراتي" },
  { key: "USD", label: "USD", ar: "دولار أمريكي" },
  { key: "GBP", label: "GBP", ar: "جنيه إسترليني" },
  { key: "EUR", label: "EUR", ar: "يورو" },
  { key: "INR", label: "INR", ar: "روبية هندية" },
];
const SHORT_AR = { AED: "درهم", USD: "دولار", GBP: "جنيه", EUR: "يورو", INR: "روبية" };

import { arCount } from "@/lib/utils";

const SYMBOLS = { AED: "AED", USD: "$", GBP: "£", EUR: "€", INR: "₹" };

// Return a short "X min ago" / "X hr ago" freshness string (Arabic when isAR).
function timeAgo(iso, isAR) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!t || Number.isNaN(t)) return null;
  const diff = Math.max(0, Date.now() - t);
  const min = Math.floor(diff / 60000);
  if (min < 1) return isAR ? "الآن" : "just now";
  if (min < 60) return isAR ? `قبل ${arCount(min, { one: "دقيقة واحدة", two: "دقيقتين", few: "دقائق", many: "دقيقة" })}` : `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return isAR ? `قبل ${arCount(hr, { one: "ساعة واحدة", two: "ساعتين", few: "ساعات", many: "ساعة" })}` : `${hr} hr ago`;
  const day = Math.floor(hr / 24);
  return isAR ? `قبل ${arCount(day, { one: "يوم واحد", two: "يومين", few: "أيام", many: "يوماً" })}` : `${day}d ago`;
}

export default function CurrencyToggle() {
  const { currency, setCurrency, rates, updatedAt } = useCurrency();
  const { isRTL } = useLang();
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ago = timeAgo(updatedAt, isRTL);
  const activeRate = rates?.[currency];
  const activeSymbol = SYMBOLS[currency] || currency;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="currency-toggle"
        className="h-10 px-3 sm:px-4 text-xs font-mono-brand text-propertic-ink hover:text-propertic-navy rounded-full border hairline hover:border-propertic-gold transition-all inline-flex items-center gap-2"
      >
        <span className="tabular">{isRTL ? SHORT_AR[currency] || currency : currency}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className={`transition-transform ${open ? "rotate-180" : ""}`}>
          <path d="M1 2.5 L4 5.5 L7 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          data-testid="currency-toggle-menu"
          className={`absolute top-full mt-2 ${isRTL ? "left-0" : "right-0"} min-w-[210px] bg-propertic-paper-2 rounded-2xl border hairline shadow-lg overflow-hidden py-1 z-50`}
        >
          {/* Live FX rate chip, reassures buyers the conversion isn't invented. */}
          {currency !== "AED" && activeRate && (
            <div
              data-testid="currency-fx-chip"
              className="px-4 py-2 border-b border-propertic-ink/10 text-[10.5px] leading-snug"
            >
              <p className="font-mono-brand text-propertic-gold tabular" dir="ltr">
                1 AED ≈ {activeSymbol} {activeRate.toFixed(activeRate < 1 ? 4 : 2)}
              </p>
              <p className="text-propertic-muted mt-0.5 font-mono-brand tracking-[0.06em]">
                {ago
                  ? (isRTL ? `حُدّث ${ago} · المصدر: open.er-api.com` : `updated ${ago} · open.er-api.com`)
                  : (isRTL ? "سعر احتياطي تقريبي، غير مُحدَّث مباشرةً" : "approximate fallback rate, not live")}
              </p>
            </div>
          )}
          {OPTIONS.map((o) => (
            <button
              key={o.key}
              type="button"
              onClick={() => {
                setCurrency(o.key);
                setOpen(false);
              }}
              data-testid={`currency-option-${o.key}`}
              className={`w-full px-4 py-2 text-xs font-mono-brand text-start transition-colors ${
                currency === o.key
                  ? "bg-propertic-navy text-propertic-paper"
                  : "text-propertic-ink hover:bg-propertic-sand/60"
              }`}
            >
              {isRTL ? <span className="flex justify-between gap-3"><span>{o.ar}</span><span className="opacity-60">{o.label}</span></span> : o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
